import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  IconButton,
  Grid,
  Chip,
  Stack,
  Divider,
  Paper,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  RotateLeft,
  RotateRight,
  Brightness6,
  Contrast,
  Close,
  Save,
  Undo,
  AspectRatio
} from '@mui/icons-material';
import { extractAllExifData, storeExifDataBackup } from '../utils/exifUtils';
import { getCroppedImg, applyImageFilters, embedExifData } from '../utils/imageProcessingUtils';

const ASPECT_RATIOS = [
  { label: 'Original', value: undefined },
  { label: 'Square', value: 1 },
];

const FILTERS = [
  { label: 'None', value: 'none' },
  { label: 'Grayscale', value: 'grayscale' },
  { label: 'Sepia', value: 'sepia' },
  { label: 'Vintage', value: 'vintage' },
  { label: 'High Contrast', value: 'highContrast' },
  { label: 'Warm', value: 'warm' },
  { label: 'Cool', value: 'cool' }
];

// Custom Crop Component
const CustomCropBox = ({ 
  imageSrc, 
  aspect,
  zoom, 
  rotation, 
  brightness, 
  contrast, 
  selectedFilter, 
  onCropChange,
  containerRef,
  onZoomChange,
  resetTrigger
}) => {
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null); // 'move', 'resize-nw', 'resize-n', 'image', etc.
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialCropBox, setInitialCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [initialImagePosition, setInitialImagePosition] = useState({ x: 0, y: 0 });
  const [aspectLocked, setAspectLocked] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  useEffect(() => {
    if (!imageSize.width || !imageSize.height) return;

    const imageContainerWidth = imageSize.width;
    const imageContainerHeight = imageSize.height;
    const imageAspect = imageContainerWidth / imageContainerHeight;

    setAspectLocked(!!aspect);

    const newAspectRatio = aspect || imageAspect;
    setAspectRatio(newAspectRatio);

    let newWidth, newHeight;

    if (newAspectRatio > imageAspect) {
      newWidth = imageContainerWidth;
      newHeight = imageContainerWidth / newAspectRatio;
    } else {
      newHeight = imageContainerHeight;
      newWidth = imageContainerHeight * newAspectRatio;
    }

    const newCropBox = {
      width: newWidth,
      height: newHeight,
      x: imagePosition.x + (imageContainerWidth - newWidth) / 2,
      y: imagePosition.y + (imageContainerHeight - newHeight) / 2,
    };
    setCropBox(newCropBox);
  }, [aspect, imageSize, imagePosition]);

  const imageRef = useRef(null);
  const cropBoxRef = useRef(null);
  const containerElementRef = useRef(null);

  // Handle image load and initialize crop box to full image size
  const handleImageLoad = useCallback(() => {
    if (imageRef.current && containerRef.current) {
      const img = imageRef.current;
      const container = containerRef.current;
      
      // Get the actual displayed size of the image
      const imgRect = img.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      // Calculate image position relative to container (centered)
      const imageWidth = Math.round(imgRect.width);
      const imageHeight = Math.round(imgRect.height);
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // Center the image in the container
      const imageX = (containerWidth - imageWidth) / 2;
      const imageY = (containerHeight - imageHeight) / 2;
      
      setImageSize({ width: imageWidth, height: imageHeight });
      setImagePosition({ x: imageX, y: imageY });
      
      // Initialize crop box to cover entire image
      const initialCrop = {
        x: imageX,
        y: imageY,
        width: imageWidth,
        height: imageHeight
      };
      
      setCropBox(initialCrop);
      setAspectRatio(imageWidth / imageHeight);
      
      // Notify parent of initial crop
      onCropChange({
        x: 0,
        y: 0,
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    }
  }, [onCropChange, containerRef]);

  // Convert crop box coordinates to image coordinates for processing
  const getCropPixels = useCallback(() => {
    if (!imageRef.current) return null;
    
    const img = imageRef.current;
    
    // Calculate the actual displayed image bounds considering zoom
    const actualImageWidth = imageSize.width * zoom;
    const actualImageHeight = imageSize.height * zoom;
    
    // Calculate the image position considering zoom (image scales from center)
    const scaledImageX = imagePosition.x - (actualImageWidth - imageSize.width) / 2;
    const scaledImageY = imagePosition.y - (actualImageHeight - imageSize.height) / 2;
    
    // Scale factors from displayed image to natural image size
    const scaleX = img.naturalWidth / actualImageWidth;
    const scaleY = img.naturalHeight / actualImageHeight;
    
    // Calculate crop coordinates relative to the scaled image
    const cropX = (cropBox.x - scaledImageX) * scaleX;
    const cropY = (cropBox.y - scaledImageY) * scaleY;
    const cropWidth = cropBox.width * scaleX;
    const cropHeight = cropBox.height * scaleY;
    
    // Validate and clamp coordinates to ensure they're within image bounds
    const validatedCrop = {
      x: Math.max(0, Math.min(img.naturalWidth - 1, Math.round(cropX))),
      y: Math.max(0, Math.min(img.naturalHeight - 1, Math.round(cropY))),
      width: Math.max(1, Math.min(img.naturalWidth, Math.round(cropWidth))),
      height: Math.max(1, Math.min(img.naturalHeight, Math.round(cropHeight)))
    };
    
    // Ensure crop doesn't extend beyond image bounds
    if (validatedCrop.x + validatedCrop.width > img.naturalWidth) {
      validatedCrop.width = img.naturalWidth - validatedCrop.x;
    }
    if (validatedCrop.y + validatedCrop.height > img.naturalHeight) {
      validatedCrop.height = img.naturalHeight - validatedCrop.y;
    }
    
    console.log("🟡 getCropPixels - Crop calculation:", {
      original: { x: cropX, y: cropY, width: cropWidth, height: cropHeight },
      validated: validatedCrop,
      imageNaturalSize: { width: img.naturalWidth, height: img.naturalHeight },
      zoom,
      imagePosition,
      cropBox
    });
    
    return validatedCrop;
  }, [cropBox, imageSize, imagePosition, zoom]);

  // Update parent when crop changes
  useEffect(() => {
    const cropPixels = getCropPixels();
    if (cropPixels) {
      onCropChange(cropPixels);
    }
  }, [cropBox, getCropPixels, onCropChange]);

  // Mouse wheel handler for zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.5, Math.min(3, zoom + delta));
    if (onZoomChange) {
      onZoomChange(newZoom);
    }
  }, [zoom, onZoomChange]);

  // Mouse down handler for starting drag operations
  const handleMouseDown = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    setDragType(type);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialCropBox({ ...cropBox });
    setInitialImagePosition({ ...imagePosition });
    
    if (aspectLocked && cropBox.width > 0 && cropBox.height > 0) {
      setAspectRatio(cropBox.width / cropBox.height);
    }
  }, [cropBox, imagePosition, aspectLocked]);

  // Image mouse down handler for panning
  const handleImageMouseDown = useCallback((e) => {
    // Only start image dragging if not clicking on crop box or handles
    if (e.target === imageRef.current) {
      e.preventDefault();
      e.stopPropagation();
      
      setIsDragging(true);
      setDragType('image');
      setDragStart({ x: e.clientX, y: e.clientY });
      setInitialImagePosition({ ...imagePosition });
    }
  }, [imagePosition]);

  // Mouse move handler for drag operations
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !dragType) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    if (dragType === 'image') {
      // Handle image panning
      const newImagePosition = {
        x: initialImagePosition.x + deltaX,
        y: initialImagePosition.y + deltaY
      };
      setImagePosition(newImagePosition);
      
      // Don't move the crop box with the image - keep it stationary
      // The crop box should stay in place while the image moves underneath it
      
      return;
    }
    
    let newCropBox = { ...initialCropBox };
    
    if (!imageRef.current || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const imgRect = imageRef.current.getBoundingClientRect();
    
    // Calculate the actual displayed image bounds considering zoom
    const actualImageWidth = imageSize.width * zoom;
    const actualImageHeight = imageSize.height * zoom;
    
    // Calculate the image position considering zoom (image scales from center)
    const scaledImageX = imagePosition.x - (actualImageWidth - imageSize.width) / 2;
    const scaledImageY = imagePosition.y - (actualImageHeight - imageSize.height) / 2;
    
    const imageX = scaledImageX;
    const imageY = scaledImageY;
    const maxX = imageX + actualImageWidth;
    const maxY = imageY + actualImageHeight;
    
    switch (dragType) {
      case 'move':
        newCropBox.x = Math.max(imageX, Math.min(maxX - newCropBox.width, initialCropBox.x + deltaX));
        newCropBox.y = Math.max(imageY, Math.min(maxY - newCropBox.height, initialCropBox.y + deltaY));
        break;
        
      case 'resize-nw':
        const newX = Math.max(imageX, Math.min(initialCropBox.x + initialCropBox.width - 20, initialCropBox.x + deltaX));
        const newY = Math.max(imageY, Math.min(initialCropBox.y + initialCropBox.height - 20, initialCropBox.y + deltaY));
        newCropBox.width = initialCropBox.width - (newX - initialCropBox.x);
        newCropBox.height = initialCropBox.height - (newY - initialCropBox.y);
        
        if (aspectLocked) {
          const targetWidth = newCropBox.height * aspectRatio;
          if (targetWidth <= initialCropBox.x + initialCropBox.width - imageX) {
            newCropBox.width = targetWidth;
            newCropBox.x = initialCropBox.x + initialCropBox.width - newCropBox.width;
          } else {
            newCropBox.height = newCropBox.width / aspectRatio;
            newCropBox.y = initialCropBox.y + initialCropBox.height - newCropBox.height;
          }
        } else {
          newCropBox.x = newX;
          newCropBox.y = newY;
        }
        break;
        
      case 'resize-ne':
        const newY2 = Math.max(imageY, Math.min(initialCropBox.y + initialCropBox.height - 20, initialCropBox.y + deltaY));
        newCropBox.width = Math.max(20, Math.min(maxX - initialCropBox.x, initialCropBox.width + deltaX));
        newCropBox.height = initialCropBox.height - (newY2 - initialCropBox.y);
        
        if (aspectLocked) {
          const targetHeight = newCropBox.width / aspectRatio;
          if (targetHeight <= initialCropBox.y + initialCropBox.height - imageY) {
            newCropBox.height = targetHeight;
            newCropBox.y = initialCropBox.y + initialCropBox.height - newCropBox.height;
          } else {
            newCropBox.width = newCropBox.height * aspectRatio;
          }
        } else {
          newCropBox.y = newY2;
        }
        break;
        
      case 'resize-sw':
        const newX2 = Math.max(imageX, Math.min(initialCropBox.x + initialCropBox.width - 20, initialCropBox.x + deltaX));
        newCropBox.width = initialCropBox.width - (newX2 - initialCropBox.x);
        newCropBox.height = Math.max(20, Math.min(maxY - initialCropBox.y, initialCropBox.height + deltaY));
        
        if (aspectLocked) {
          const targetWidth = newCropBox.height * aspectRatio;
          if (targetWidth <= initialCropBox.x + initialCropBox.width - imageX) {
            newCropBox.width = targetWidth;
            newCropBox.x = initialCropBox.x + initialCropBox.width - newCropBox.width;
          } else {
            newCropBox.height = newCropBox.width / aspectRatio;
          }
        } else {
          newCropBox.x = newX2;
        }
        break;
        
      case 'resize-se':
        newCropBox.width = Math.max(20, Math.min(maxX - initialCropBox.x, initialCropBox.width + deltaX));
        newCropBox.height = Math.max(20, Math.min(maxY - initialCropBox.y, initialCropBox.height + deltaY));
        
        if (aspectLocked) {
          const targetHeight = newCropBox.width / aspectRatio;
          if (targetHeight <= maxY - initialCropBox.y) {
            newCropBox.height = targetHeight;
          } else {
            newCropBox.width = newCropBox.height * aspectRatio;
          }
        }
        break;
        
      case 'resize-n':
        const newY3 = Math.max(imageY, Math.min(initialCropBox.y + initialCropBox.height - 20, initialCropBox.y + deltaY));
        newCropBox.height = initialCropBox.height - (newY3 - initialCropBox.y);
        if (!aspectLocked) {
          newCropBox.y = newY3;
        }
        break;
        
      case 'resize-s':
        newCropBox.height = Math.max(20, Math.min(maxY - initialCropBox.y, initialCropBox.height + deltaY));
        break;
        
      case 'resize-w':
        const newX3 = Math.max(imageX, Math.min(initialCropBox.x + initialCropBox.width - 20, initialCropBox.x + deltaX));
        newCropBox.width = initialCropBox.width - (newX3 - initialCropBox.x);
        if (!aspectLocked) {
          newCropBox.x = newX3;
        }
        break;
        
      case 'resize-e':
        newCropBox.width = Math.max(20, Math.min(maxX - initialCropBox.x, initialCropBox.width + deltaX));
        break;
    }
    
    setCropBox(newCropBox);
  }, [isDragging, dragType, dragStart, initialCropBox, initialImagePosition, imageSize, imagePosition, zoom, aspectLocked, aspectRatio, containerRef]);

  // Mouse up handler
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
  }, []);

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Add wheel event listener for zoom
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        if (container) {
          container.removeEventListener('wheel', handleWheel);
        }
      };
    }
  }, [handleWheel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any pending operations
      setIsDragging(false);
      setDragType(null);
    };
  }, []);

  // Handle reset trigger
  useEffect(() => {
    if (resetTrigger) {
      // Re-trigger image load to reset position and crop box
      handleImageLoad();
    }
  }, [resetTrigger, handleImageLoad]);

  // Apply image transformations with positioning
  const imageStyle = {
    position: 'absolute',
    left: imagePosition.x,
    top: imagePosition.y,
    transform: `scale(${zoom}) rotate(${rotation}deg)`,
    transformOrigin: 'center center',
    filter: `brightness(${100 + brightness}%) contrast(${100 + contrast}%)`,
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    cursor: isDragging && dragType === 'image' ? 'grabbing' : 'grab'
  };

  // Apply filter effects
  if (selectedFilter !== 'none') {
    switch (selectedFilter) {
      case 'grayscale':
        imageStyle.filter += ' grayscale(100%)';
        break;
      case 'sepia':
        imageStyle.filter += ' sepia(100%)';
        break;
      case 'vintage':
        imageStyle.filter += ' sepia(50%) contrast(120%) brightness(90%)';
        break;
      case 'highContrast':
        imageStyle.filter += ' contrast(150%)';
        break;
      case 'warm':
        imageStyle.filter += ' sepia(20%) saturate(120%)';
        break;
      case 'cool':
        imageStyle.filter += ' hue-rotate(180deg) saturate(120%)';
        break;
    }
  }

  return (
    <Box 
      sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
      onMouseDown={(e) => {
        // Handle background clicks for image panning
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          
          setIsDragging(true);
          setDragType('image');
          setDragStart({ x: e.clientX, y: e.clientY });
          setInitialImagePosition({ ...imagePosition });
        }
      }}
    >
      {/* Image */}
      <img
        ref={imageRef}
        src={imageSrc}
        alt="Crop preview"
        style={imageStyle}
        onLoad={handleImageLoad}
        onMouseDown={(e) => {
          // Handle direct image clicks for panning
          e.preventDefault();
          e.stopPropagation();
          
          setIsDragging(true);
          setDragType('image');
          setDragStart({ x: e.clientX, y: e.clientY });
          setInitialImagePosition({ ...imagePosition });
        }}
        draggable={false}
      />
      
      {/* Crop Overlay */}
      {cropBox.width > 0 && cropBox.height > 0 && (
        <>
          {/* Dimmed overlay outside crop area using CSS clip-path */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              clipPath: `polygon(
                0% 0%, 
                0% 100%, 
                ${cropBox.x}px 100%, 
                ${cropBox.x}px ${cropBox.y}px, 
                ${cropBox.x + cropBox.width}px ${cropBox.y}px, 
                ${cropBox.x + cropBox.width}px ${cropBox.y + cropBox.height}px, 
                ${cropBox.x}px ${cropBox.y + cropBox.height}px, 
                ${cropBox.x}px 100%, 
                100% 100%, 
                100% 0%
              )`,
              pointerEvents: 'none'
            }}
          />
          
          {/* Crop box with handles */}
          <Box
            ref={cropBoxRef}
            sx={{
              position: 'absolute',
              left: cropBox.x,
              top: cropBox.y,
              width: cropBox.width,
              height: cropBox.height,
              pointerEvents: 'none' // Allow clicks to pass through to inner elements
            }}
          >
            {/* Crop box border (clickable for moving) */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '2px solid #fff',
                pointerEvents: 'auto',
                cursor: 'move',
                '&:hover': {
                  border: '2px solid #2196f3'
                },
                // Create a border-only box by making the inner area transparent to clicks
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '6px',
                  left: '6px',
                  right: '6px',
                  bottom: '6px',
                  pointerEvents: 'none', // Inner area doesn't block image interaction
                  backgroundColor: 'transparent'
                }
              }}
              onMouseDown={(e) => {
                // Only handle border clicks, not inner area clicks
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const borderWidth = 6; // Effective border click area
                
                // Check if click is on the border area (not inner area)
                const isOnBorder = x < borderWidth || 
                                 y < borderWidth || 
                                 x > rect.width - borderWidth || 
                                 y > rect.height - borderWidth;
                
                if (isOnBorder) {
                  handleMouseDown(e, 'move');
                }
              }}
            />
            
            {/* Inner area for image panning */}
            <Box
              sx={{
                position: 'absolute',
                top: '6px',
                left: '6px',
                right: '6px',
                bottom: '6px',
                cursor: isDragging && dragType === 'image' ? 'grabbing' : 'grab',
                pointerEvents: 'auto',
                backgroundColor: 'transparent'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                setIsDragging(true);
                setDragType('image');
                setDragStart({ x: e.clientX, y: e.clientY });
                setInitialImagePosition({ ...imagePosition });
              }}
            />
            {/* Corner handles */}
            {['nw', 'ne', 'sw', 'se'].map((corner) => (
              <Box
                key={corner}
                sx={{
                  position: 'absolute',
                  width: 12,
                  height: 12,
                  backgroundColor: '#2196f3',
                  border: '2px solid #fff',
                  borderRadius: '50%',
                  cursor: `${corner}-resize`,
                  zIndex: 10,
                  pointerEvents: 'auto',
                  ...(corner === 'nw' && { top: -6, left: -6 }),
                  ...(corner === 'ne' && { top: -6, right: -6 }),
                  ...(corner === 'sw' && { bottom: -6, left: -6 }),
                  ...(corner === 'se' && { bottom: -6, right: -6 }),
                  '&:hover': {
                    backgroundColor: '#1976d2',
                    transform: 'scale(1.2)'
                  }
                }}
                onMouseDown={(e) => handleMouseDown(e, `resize-${corner}`)}
              />
            ))}
            
            {/* Edge handles */}
            {['n', 'e', 's', 'w'].map((edge) => (
              <Box
                key={edge}
                sx={{
                  position: 'absolute',
                  backgroundColor: '#2196f3',
                  border: '1px solid #fff',
                  cursor: `${edge}-resize`,
                  zIndex: 10,
                  pointerEvents: 'auto',
                  ...(edge === 'n' && { 
                    top: -4, 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    width: 20, 
                    height: 8 
                  }),
                  ...(edge === 's' && { 
                    bottom: -4, 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    width: 20, 
                    height: 8 
                  }),
                  ...(edge === 'w' && { 
                    left: -4, 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    width: 8, 
                    height: 20 
                  }),
                  ...(edge === 'e' && { 
                    right: -4, 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    width: 8, 
                    height: 20 
                  }),
                  '&:hover': {
                    backgroundColor: '#1976d2',
                    transform: edge === 'n' || edge === 's' 
                      ? 'translateX(-50%) scale(1.2)' 
                      : 'translateY(-50%) scale(1.2)'
                  }
                }}
                onMouseDown={(e) => handleMouseDown(e, `resize-${edge}`)}
              />
            ))}
          </Box>
          
          {/* Crop dimensions display */}
          <Box
            sx={{
              position: 'absolute',
              left: cropBox.x,
              top: cropBox.y - 30,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 1,
              fontSize: '12px',
              pointerEvents: 'none'
            }}
          >
            {Math.round(cropBox.width)} × {Math.round(cropBox.height)}
          </Box>
        </>
      )}
      
      {/* Aspect ratio lock toggle */}
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderRadius: 1,
          padding: 1
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={aspectLocked}
              onChange={(e) => setAspectLocked(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="caption" sx={{ color: 'white' }}>
              Lock Aspect
            </Typography>
          }
        />
      </Box>
    </Box>
  );
};

const ImageCropModal = ({
  open,
  onClose,
  imageSrc,
  onSave,
  originalFileName
}) => {
  // Crop states
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(undefined);

  // Adjustment states
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState('none');

  // Processing states
  const [processing, setProcessing] = useState(false);
  const [originalExifData, setOriginalExifData] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [originalFormat, setOriginalFormat] = useState('image/jpeg');

  // Reset trigger
  const [resetTrigger, setResetTrigger] = useState(0);

  // Refs
  const containerRef = useRef(null);
  const fileId = useRef(Date.now().toString());

  // Extract EXIF data when modal opens
  useEffect(() => {
    if (open && imageSrc) {
      extractExifFromImage();
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  // Generate preview when adjustments change
  useEffect(() => {
    if (imageSrc && (brightness !== 0 || contrast !== 0 || selectedFilter !== 'none')) {
      generatePreview();
    } else {
      setPreviewImage(null);
    }
  }, [brightness, contrast, selectedFilter, imageSrc]);

  const extractExifFromImage = async () => {
    try {
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const file = new File([blob], originalFileName || 'image.jpg', { type: blob.type });
      
      let detectedFormat = blob.type;
      if (!detectedFormat || detectedFormat === 'application/octet-stream') {
        const extension = originalFileName?.toLowerCase().split('.').pop();
        switch (extension) {
          case 'png':
            detectedFormat = 'image/png';
            break;
          case 'jpg':
          case 'jpeg':
            detectedFormat = 'image/jpeg';
            break;
          case 'webp':
            detectedFormat = 'image/webp';
            break;
          default:
            detectedFormat = 'image/png';
        }
      }
      
      setOriginalFormat(detectedFormat);
      console.log('🟡 MODAL - Detected original format:', detectedFormat);
      
      const exifData = await extractAllExifData(file);
      setOriginalExifData(exifData);
      
      storeExifDataBackup(fileId.current, exifData);
      
      console.log('EXIF data extracted and backed up:', exifData);
    } catch (error) {
      console.error('Error extracting EXIF data:', error);
    }
  };

  const generatePreview = useCallback(async () => {
    if (!imageSrc) return;

    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        applyImageFilters(ctx, img, {
          brightness,
          contrast,
          filter: selectedFilter
        });
        
        const previewDataUrl = originalFormat === 'image/png' 
          ? canvas.toDataURL(originalFormat)
          : canvas.toDataURL(originalFormat, 0.9);
        
        setPreviewImage(previewDataUrl);
      };
      img.src = imageSrc;
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  }, [imageSrc, brightness, contrast, selectedFilter, originalFormat]);

  const onCropComplete = useCallback((croppedAreaPixels) => {
    console.log("🟡 CROP COMPLETE - Crop area updated:", croppedAreaPixels);
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleRotateLeft = () => {
    setRotation(prev => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation(prev => prev + 90);
  };

  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setBrightness(0);
    setContrast(0);
    setSelectedFilter('none');
    setAspect(undefined);
    // Trigger reset in CustomCropBox to reset image position and crop box
    setResetTrigger(prev => prev + 1);
  };

  const handleSave = async () => {
    if (!croppedAreaPixels || croppedAreaPixels.width <= 0 || croppedAreaPixels.height <= 0) {
      console.error("🟡 MODAL SAVE - Invalid crop area:", croppedAreaPixels);
      alert("Please select a valid crop area before saving.");
      return;
    }

    setProcessing(true);
    try {
      console.log("🟡 MODAL SAVE - Starting image processing...");
      
      const imageToProcess = previewImage || imageSrc;
      console.log("🟡 MODAL SAVE - Image to process:", {
        source: previewImage ? "preview" : "original",
        url: imageToProcess ? imageToProcess.substring(0, 100) + "..." : "null"
      });
      
      const croppedImage = await getCroppedImg(
        imageToProcess,
        croppedAreaPixels,
        rotation,
        originalFormat,
        0.9
      );
      console.log("🟡 MODAL SAVE - Cropped image:", {
        url: croppedImage ? croppedImage.substring(0, 100) + "..." : "null",
        length: croppedImage ? croppedImage.length : 0
      });

      let finalImage = croppedImage;

      console.log("🟡 MODAL SAVE - Converting to File object...");
      const response = await fetch(finalImage);
      const blob = await response.blob();
      
      let fileName = originalFileName || 'edited-image';
      if (fileName.includes('.')) {
        fileName = fileName.substring(0, fileName.lastIndexOf('.'));
      }
      
      const extension = originalFormat === 'image/png' ? '.png' : 
                       originalFormat === 'image/webp' ? '.webp' : '.jpg';
      fileName += extension;
      
      const processedFile = new File([blob], fileName, {
        type: originalFormat
      });
      
      console.log("🟡 MODAL SAVE - Created File object:", {
        name: processedFile.name,
        size: processedFile.size,
        type: processedFile.type
      });

      const resultData = {
        preview: finalImage,
        raw: processedFile,
        exifData: originalExifData
      };
      
      console.log("🟡 MODAL SAVE - Calling onSave with:", {
        preview: resultData.preview ? resultData.preview.substring(0, 100) + "..." : "null",
        previewLength: resultData.preview ? resultData.preview.length : 0,
        rawFile: resultData.raw ? {
          name: resultData.raw.name,
          size: resultData.raw.size,
          type: resultData.raw.type
        } : "null",
        exifData: resultData.exifData ? "present" : "null"
      });

      onSave(resultData);
      handleClose();
    } catch (error) {
      console.error('🟡 MODAL SAVE - Error processing image:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    handleReset();
    setOriginalExifData(null);
    setPreviewImage(null);
    onClose();
  };

  const displayImage = previewImage || imageSrc;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Edit Image</Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'row', p: 2, gap: 2, height: '100%' }}>
        {/* Crop Area - Left Side */}
        <Box 
          ref={containerRef}
          sx={{ 
            position: 'relative', 
            flex: 1, 
            minHeight: 400,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5'
          }}
        >
          <CustomCropBox
            imageSrc={displayImage}
            aspect={aspect}
            zoom={zoom}
            rotation={rotation}
            brightness={brightness}
            contrast={contrast}
            selectedFilter={selectedFilter}
            onCropChange={onCropComplete}
            containerRef={containerRef}
            onZoomChange={setZoom}
            resetTrigger={resetTrigger}
          />
        </Box>

        {/* Controls - Right Side */}
        <Paper sx={{ 
          p: 2, 
          width: 320,
          maxHeight: '100%',
          overflowY: 'auto',
          flexShrink: 0
        }}>
          <Grid container spacing={2}>
          {/* Aspect Ratio */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Aspect Ratio
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
              The image can be any aspect ratio and will be displayed properly, but it is displayed in a square space in the app.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {ASPECT_RATIOS.map((ratio) => (
                <Chip
                  key={ratio.label}
                  label={ratio.label}
                  onClick={() => setAspect(ratio.value)}
                  color={aspect === ratio.value ? 'primary' : 'default'}
                  variant={aspect === ratio.value ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </Grid>
            {/* Zoom */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Zoom: {zoom.toFixed(1)}x
              </Typography>
              <Slider
                value={zoom}
                min={0.5}
                max={3}
                step={0.1}
                onChange={(_, value) => setZoom(value)}
                valueLabelDisplay="auto"
              />
            </Grid>

            {/* Rotation */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Rotation: {rotation}°
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={handleRotateLeft} size="small">
                  <RotateLeft />
                </IconButton>
                <Slider
                  value={rotation}
                  min={-180}
                  max={180}
                  step={1}
                  onChange={(_, value) => setRotation(value)}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={handleRotateRight} size="small">
                  <RotateRight />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Brightness 
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                <Brightness6 sx={{ verticalAlign: 'middle', mr: 1 }} />
                Brightness: {brightness}
              </Typography>
              <Slider
                value={brightness}
                min={-100}
                max={100}
                step={1}
                onChange={(_, value) => setBrightness(value)}
                valueLabelDisplay="auto"
              />
            </Grid>

            {/* Contrast 
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                <Contrast sx={{ verticalAlign: 'middle', mr: 1 }} />
                Contrast: {contrast}
              </Typography>
              <Slider
                value={contrast}
                min={-100}
                max={100}
                step={1}
                onChange={(_, value) => setContrast(value)}
                valueLabelDisplay="auto"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Filters 
            <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Aspect Ratio
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
              The image can be any aspect ratio and will be displayed properly, but it is displayed in a square space in the app.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {FILTERS.map((filter) => (
                  <Chip
                    key={filter.value}
                    label={filter.label}
                    onClick={() => setSelectedFilter(filter.value)}
                    color={selectedFilter === filter.value ? 'primary' : 'default'}
                    variant={selectedFilter === filter.value ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Stack>
            </Grid> */}
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleReset}
          startIcon={<Undo />}
          variant="outlined"
        >
          Reset
        </Button>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<Save />}
          disabled={processing}
        >
          {processing ? 'Processing...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropModal;
